<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { Rive, Fit, Layout } from '@rive-app/canvas'
import { useServiceStore } from '@stores/service'
import { format } from '@formkit/tempo'
import ScrollFrame from '@components/global/accents/frames/ScrollFrame.vue'
import Underline from '@components/global/accents/Underline.vue'
import Input from '@components/global/forms/Input.vue'
import Textarea from '@components/global/forms/Textarea.vue'
import Datetime from '@components/global/forms/Datetime.vue'
import Button from '@components/global/forms/Button.vue'
import Select from '@components/global/forms/Select.vue'
import TransitionSlide from '@components/global/transitions/TransitionSlide.vue'
import Header from '@components/global/accents/Header.vue'

const props = defineProps({
	emailDirect: {
		type: Boolean,
		default: false,
	},
})
const serviceStore = useServiceStore()
const crowCanvas = ref(null)
const formStage = ref(0)
const success = ref(false)
const processing = ref(false)
const errors = ref(null)
const inquiry = ref({})
const inquiryId = ref(null)

const postInquiry = (form) => {
	form.preventDefault()
	if (!processing.value) {
		processing.value = true
		axios
			.post('/api/inquiry', inquiry.value)
			.then((r) => {
				processing.value = false
				formStage.value++
				inquiry.value.id = r.data.inquiry
			})
			.catch((error) => {
				processing.value = false
				if (error.response.status === 422) {
					errors.value = error.response.data.errors
					console.error(errors.value)
				}
			})
	}
}

Date.prototype.addDays = function (days) {
	var date = new Date(this.valueOf())
	date.setDate(date.getDate() + days)
	return date
}

watch(formStage, (newVal) => {
	if (newVal == 2) {
		nextTick(() => {
			new Rive({
				canvas: crowCanvas.value,
				src: 'https://cdn.papercrowns.com/website/rive/crow.riv',
				autoplay: true,
				layout: new Layout({
					fit: Fit.Contain,
				}),
			})
		})
	}
})
</script>

<template>
	<ScrollFrame>
		<TransitionSlide>
			<form class="flex flex-col gap-6" v-if="formStage == 0" :key="formStage" @submit="postInquiry">
				<div class="flex flex-col items-center">
					<img class="h-6 sm:h-8 lg:h-10" src="https://cdn.papercrowns.com/website/images/elements/lockups/get-in-touch.svg" alt="Get in Touch" />
					<Underline class="w-40 sm:w-52 lg:w-64" type="short" fill="#474747" />
				</div>
				<div class="flex flex-col gap-5">
					<Input label="Your Name" placeholder="Your name here" v-model="inquiry.name" autocomplete="name" :error="errors?.name && errors.name[0]" required />
					<Input label="Your Email" placeholder="Your email here" v-model="inquiry.email" autocomplete="email" :error="errors?.email && errors.email[0]" required />
					<Textarea label="Your Message" rows="6" placeholder="Describe your project, please be as detailed as possible." v-model="inquiry.message" autocomplete="off" :error="errors?.message && errors.message[0]" required />
				</div>
				<div class="flex flex-col items-center gap-5">
					<Button type="submit" label="Send" :processing="processing" iconRight="fas fa-arrow-up-right" />
					<div class="text-center text-sm text-crowns-castle-stone" v-if="emailDirect">
						Want to email us directly?
						<a class="text-crowns-off-white" href="mailto:hello@papercrowns.com">hello@papercrowns.com</a>
					</div>
				</div>
			</form>
			<form class="flex flex-col gap-8" v-else-if="formStage == 1 && inquiry.id" @submit="postInquiry">
				<div class="flex flex-col items-center gap-5">
					<img class="h-6 sm:h-8 lg:h-10" src="https://cdn.papercrowns.com/website/images/elements/lockups/before-you-go.svg" alt="Before You Go" />
					<p class="text-sm lg:text-base">Let us help you faster &amp; get you speaking with the right people.</p>
				</div>
				<div class="flex flex-col gap-5">
					<Select label="Which key services are you interested in?" placeholder="Select Services" :options="serviceStore.filterCategories" v-model="inquiry.services" />
					<Datetime label="Target Deadline" :placeholder="new Date().toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })" :auto-apply="true" :utc="true" :min-date="new Date().addDays(1)" v-model="inquiry.target_deadline" />
					<div class="flex flex-col gap-2">
						<Input label="Rough Budget" sublabel="(USD)" icon="fas fa-dollar-sign" filter="numeric" placeholder="What do we have to work with?" v-model="inquiry.budget" />
						<p class="small">We accomodate projects big and small, but giving us a ballpark idea of your budget will help us suggest and provide the best solutions for you.</p>
					</div>
				</div>
				<div class="flex flex-col items-center gap-2">
					<Button type="submit" label="Send Details" :processing="processing" iconRight="fas fa-arrow-up-right" />
				</div>
			</form>
			<div class="flex flex-col items-center gap-8" v-else-if="formStage == 2">
				<canvas class="size-32 sm:size-48 lg:size-60" height="768" width="768" ref="crowCanvas" />
				<div class="mb-10 flex flex-col items-center gap-5">
					<img class="h-6 lg:h-8" src="https://cdn.papercrowns.com/website/images/elements/lockups/thank-you.svg" alt="Thank You" />
					<p class="text-center text-sm lg:text-base">We have picked up your letter and we'll be in touch shortly.</p>
				</div>
			</div>
		</TransitionSlide>
	</ScrollFrame>
</template>
